import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";
import DigDaysDecisionModal from "./DigDaysDecisionModal";
import Loader from "../loader/Loader";
import ReactStars from "react-stars";
import {
  convertDateToLocalTime,
  truncate
} from "../../assets/utils/UtilsFunctons";
import Enums from "../../assets/JsonData/Enums";
import TablePagination from "../table/table-pagination/TablePagination";
import DigDayDurationAndLocation from "./DigDayDurationAndLocation";
import { orderBy } from "lodash";
import useUserRole from "../../hooks/useUserRole";

const DigDaysTable = ({ digDays, handleDigDayDecision }) => {
  const navigate = useNavigate();
  const { isAdmin, hasAccessAsDeveloper, isManager } = useUserRole();
  const { accounts } = useMsal();

  const { usersInfos } = useSelector((state) => ({ ...state.userInfos }));

  const filteredUsers = useMemo(() => {
    const users = usersInfos?.data ?? [];
    if (isManager && !isAdmin && !hasAccessAsDeveloper) {
      const oid = accounts[0]?.idTokenClaims?.oid;
      return users.filter((u) => u.operatingManagerId === oid);
    }
    return users;
  }, [usersInfos, isManager, isAdmin, hasAccessAsDeveloper, accounts]);

  // Retrouve la société d'un utilisateur à partir de son userId
  const userCompanyById = useMemo(() => {
    return new Map((usersInfos?.data ?? []).map((u) => [u.userId, u.company]));
  }, [usersInfos]);

  const getUserCompanyById = useCallback(
    (userId) => userCompanyById.get(userId) ?? null,
    [userCompanyById]
  );

  const filteredDigDays = useMemo(() => {
    const data = digDays?.data ?? [];
    let result = data;
    if (isManager && !isAdmin && !hasAccessAsDeveloper) {
      const myUserIds = filteredUsers.map((u) => u.userId);
      result = data.filter((dd) => myUserIds.includes(dd.userId));
    }
    // Enrichissement avec le champ "company", pour la colonne et le filtre
    return result.map((dd) => ({
      ...dd,
      company: getUserCompanyById(dd.userId),
    }));
  }, [digDays, filteredUsers, isManager, isAdmin, hasAccessAsDeveloper, getUserCompanyById]);

  // Stockage des filtres actifs et pageIndex localement
  const [activeFilters, setActiveFilters] = useState({});
  const [pageIndex, setPageIndex] = useState(0);

  // Nettoyer tous les filtres au montage pour éviter persistance après refresh
  React.useEffect(() => {
    setActiveFilters({});
  }, []);

  // Fonction pour extraire la valeur simple de date (string ou number)
  const getDateFilterValue = (dateFilter) => {
    if (dateFilter === undefined || dateFilter === null) return null;
    if (typeof dateFilter === "string" || typeof dateFilter === "number") {
      return String(dateFilter);
    }
    if (typeof dateFilter === "object") {
      if ("value" in dateFilter) return dateFilter.value;
      if ("date" in dateFilter) return dateFilter.date;
      if ("label" in dateFilter) return dateFilter.label;
      return JSON.stringify(dateFilter);
    }
    return null;
  };

  // Mise à jour des filtres venant du composant TablePagination
  const handleFilterChange = useCallback((filters) => {
    console.log("Filters received:", filters);
    setActiveFilters(filters);
    setPageIndex(0);
  }, []);

  const sortedDigDays = useCallback(() => {
    return orderBy(filteredDigDays, ["date", "fullName"], ["desc", "asc"]);
  }, [filteredDigDays]);

  const digDayActionsBasedOnState = (digDay) => {
    if (digDay.state === 0) {
      return (
        <>
          <DigDaysDecisionModal
            decision="cancel"
            digDay={digDay}
            handleDigDayDecision={handleDigDayDecision}
          />
        </>
      );
    }
    if (digDay.state === 1 || digDay.state === 3) {
      return (
        <>
          <DigDaysDecisionModal
            decision="notification"
            digDay={digDay}
            handleDigDayDecision={handleDigDayDecision}
          />
        </>
      );
    }
    if (digDay.state === 2) {
      return (
        <>
          <DigDaysDecisionModal
            decision="validation"
            digDay={digDay}
            handleDigDayDecision={handleDigDayDecision}
          />
          <DigDaysDecisionModal
            decision="adjustment"
            digDay={digDay}
            handleDigDayDecision={handleDigDayDecision}
          />
          <DigDaysDecisionModal
            decision="rejection"
            digDay={digDay}
            handleDigDayDecision={handleDigDayDecision}
          />
        </>
      );
    }
  };

  const columns = [
    {
      Header: "Date",
      accessor: "date",
      Cell: ({ value }) => (value ? convertDateToLocalTime(value) : "")
    },
    {
      Header: "Statut",
      accessor: "state",
      Cell: ({ value }) => Enums.matchDigDayState(value)
    },
    {
      Header: "Collaborateur",
      accessor: "fullName"
    },
    {
      Header: "Société",
      accessor: "company",
      Cell: ({ value }) => Enums.matchCompany(value)
    },
    {
      Header: "Titre",
      accessor: "title",
      Cell: ({ value }) => truncate(value, 18)
    },
    {
      Header: "Description",
      accessor: "description",
      Cell: ({ value }) => truncate(value, 18)
    },
    {
      Header: "Durée/Lieu",
      Cell: ({ row }) => <DigDayDurationAndLocation digDay={row?.original} />
    },
    {
      Header: "Evaluation",
      accessor: "rating",
      Cell: ({ value }) =>
        value ? (
          <ReactStars count={5} size={24} value={value} edit={false} />
        ) : (
          ""
        )
    },
    {
      Header: "Actions",
      Cell: ({ row }) => (
        <div className="d-flex justify-content-evenly">
          {(isAdmin || hasAccessAsDeveloper || isManager) &&
            digDayActionsBasedOnState(row.original)}
        </div>
      )
    }
  ];

  /* eslint @typescript-eslint/no-empty-function: "off", @typescript-eslint/no-unused-vars: "off" */
  const tableHooks = (hooks) => {};

  const handleRowClicked = (row) => {
    navigate("/DigDayDetailPage/" + row.original.digDayId);
  };

  const userIdDisplayValueFunc = useCallback(
    (userId) => {
      const user = filteredUsers.find((u) => u.userId === userId);
      return user ? user.firstName + " " + user.lastName : null;
    },
    [filteredUsers]
  );

  const questStateItemDisplayValueFunc = useCallback((digDayState) => {
    return Enums.matchDigDayState(digDayState);
  }, []);

  const monthDisplayValueFunc = useCallback((monthNumber) => {
    return Enums.getMonthLabel(monthNumber);
  }, []);

  const getCompaniesList = useCallback(() => {
    return [
      { value: 0, label: "Diggers" },
      { value: 1, label: "Finaura" }
    ];
  }, []);

  const companyDisplayValueFunc = useCallback((company) => {
    if (company == null) return "-";
    if (typeof company === "object" && company.label) return company.label;
    return Enums.matchCompany(company);
  }, []);

  const getDefaultValueData = useCallback((data) => {
    if (data) {
      return data.filter((digDay) => digDay.state === 2);
    }
    return null;
  }, []);

  const dateFilterCleanValue = getDateFilterValue(activeFilters.date);
  const isDateSelected =
    dateFilterCleanValue && dateFilterCleanValue.length > 0;

  const onPageIndexChange = useCallback(() => setPageIndex(0), []);

  return (
    <div>
      {digDays.data ? (
        <TablePagination
          data={sortedDigDays() ?? []}
          columns={columns}
          tableHooks={tableHooks}
          refresh={
            digDays?.data ||
            isAdmin ||
            !hasAccessAsDeveloper ||
            userIdDisplayValueFunc
          }
          handleRowClicked={handleRowClicked}
          type={"default"}
          enableFilter={true}
          hasDefaultValue={true}
          getDefaultValueData={getDefaultValueData}
          onFilterChange={handleFilterChange}
          onPageIndexChange={onPageIndexChange}
          initialPageIndex={pageIndex}>
          <element
            key="state"
            filterKey="state"
            className="cell"
            casesensitive={"true"}
            showsearch={"true"}
            isMulti
            itemDisplayValueFunc={questStateItemDisplayValueFunc}
            hasDefaultValue={true}
            defaultValue={2}>
            Statut
          </element>
          <element
            key="userId"
            filterKey="userId"
            className="cell"
            casesensitive={"true"}
            showsearch={"true"}
            itemDisplayValueFunc={userIdDisplayValueFunc}>
            Collab.
          </element>
          <element
            key="company"
            filterKey="company"
            className="cell"
            casesensitive={"true"}
            showsearch={"true"}
            data={getCompaniesList()}
            itemDisplayValueFunc={companyDisplayValueFunc}>
            Société
          </element>
          <element
            key="date"
            filterKey="date"
            className="cell"
            casesensitive={"true"}
            showsearch={"true"}
            isDate={true}>
            Année
          </element>

          {isDateSelected && (
            <element
              key="month"
              filterKey="month"
              className="cell"
              casesensitive={"true"}
              showsearch={"true"}
              isSelect={true}
              itemDisplayValueFunc={monthDisplayValueFunc}
              options={Enums.getMonthsOptions()}>
              Mois
            </element>
          )}
        </TablePagination>
      ) : (
        <Loader />
      )}
    </div>
  );
};

export default DigDaysTable;
